import React, { useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Users, Mail, BadgeCheck, TrendingUp, AlertTriangle, Sparkles, Hourglass } from 'lucide-react';

const STALE_MS = 14 * 86400000;

// Overview cards for the admin dashboard: consumer invite funnel (invited →
// registered, conversion %, stale invites) and loyalty seeding status
// (seeded vs unseeded accounts). Mirrors the stats shown in Consumer Accounts.
export default function ConsumerFunnelOverview() {
  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['dash-consumer-accounts'],
    queryFn: () => base44.entities.ConsumerAccount.list('-created_date', 500),
  });
  const accountIds = useMemo(() => (accounts || []).map((a) => a.id), [accounts]);
  const { data: seedData = [] } = useQuery({
    queryKey: ['dash-consumer-seed', accountIds.length],
    queryFn: () => base44.functions.invoke('consumerAccountsBulkAction', { mode: 'seed_status', account_ids: accountIds }).then((r) => r.data?.results || []),
    enabled: accountIds.length > 0,
  });

  const stats = useMemo(() => {
    const all = accounts || [];
    const invited = all.filter((a) => a.invited_at).length;
    const registered = all.filter((a) => a.linked_user_id).length;
    const conversion = invited ? Math.round((registered / invited) * 100) : 0;
    const stale = all.filter((a) => a.invited_at && !a.linked_user_id && (Date.now() - new Date(a.invited_at).getTime()) > STALE_MS).length;
    const seeded = (seedData || []).filter((s) => s.seeded_points > 0).length;
    const unseeded = (seedData || []).filter((s) => s.has_paid_orders && !s.seeded_points).length;
    return { total: all.length, invited, registered, conversion, stale, seeded, unseeded };
  }, [accounts, seedData]);

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  }

  const cards = [
    { label: 'CONSUMER ACCOUNTS', value: stats.total, icon: Users, accent: 'text-white' },
    { label: 'INVITED', value: stats.invited, icon: Mail, accent: 'text-green-400' },
    { label: 'REGISTERED', value: stats.registered, icon: BadgeCheck, accent: 'text-primary' },
    { label: 'CONVERSION', value: `${stats.conversion}%`, icon: TrendingUp, accent: 'text-primary' },
    { label: 'STALE INVITES', value: stats.stale, icon: AlertTriangle, accent: 'text-amber-400', attention: stats.stale > 0 },
    { label: 'LOYALTY SEEDED', value: stats.seeded, icon: Sparkles, accent: 'text-amber-400' },
    { label: 'UNSEEDED', value: stats.unseeded, icon: Hourglass, accent: 'text-cyan-400', attention: stats.unseeded > 0 },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
      {cards.map((c) => (
        <div key={c.label} className={`border bg-[#111115] p-4 text-center ${c.attention ? 'attention-amber' : 'border-white/10'}`}>
          <c.icon className={`w-4 h-4 mx-auto mb-2 ${c.accent}`} />
          <p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">{c.label}</p>
          <p className={`${c.accent} font-black text-2xl`}>{c.value}</p>
        </div>
      ))}
    </div>
  );
}